import { createContext, useContext, useRef, useSyncExternalStore } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { SpecsCatalog } from "./specsModel/catalog";
import { SpecsModel } from "./specsModel/SpecsModel";

function createScratchSpecsModel(versionName: string, specs: SpecsCatalog) {
  return SpecsModel.fromSnapshot({
    name: "",
    versions: [
      {
        name: versionName || "1.0",
        parts: [],
        positions: [],
        positionGroups: [],
        generator: { sides: [] },
      },
    ],
  }).setValidationContext({
    fabrics: specs.fabrics,
  });
}

export type SpecsModelContextValue = {
  specsModel: SpecsModel | null;
  setSpecsModel: Dispatch<SetStateAction<SpecsModel | null>>;
};

export const SpecsModelContext = createContext<SpecsModelContextValue | null>(
  null
);

export function useSpecsModelContextValue() {
  const context = useContext(SpecsModelContext);

  if (!context) {
    throw new Error(
      "useSpecsModelContextValue must be used within a SpecsModelContext provider"
    );
  }

  return context;
}

export function useSpecsModel(): SpecsModel {
  const { specsModel } = useSpecsModelContextValue();

  if (!specsModel) {
    throw new Error("SpecsModel is not available in SpecsModelContext");
  }

  return specsModel;
}

export function useSpecsModelSelector<T>(
  selector: (model: SpecsModel) => T,
  isEqual: (left: T, right: T) => boolean = Object.is
): T {
  const model = useSpecsModel();
  const cacheRef = useRef<{
    revision: number;
    selector: (model: SpecsModel) => T;
    value: T;
  } | null>(null);

  return useSyncExternalStore(
    (onStoreChange) => model.subscribe(onStoreChange),
    () => {
      const revision = model.getRevision();
      const cached = cacheRef.current;

      if (
        cached &&
        cached.revision === revision &&
        cached.selector === selector
      ) {
        return cached.value;
      }

      const nextValue = selector(model);

      if (cached && isEqual(cached.value, nextValue)) {
        cacheRef.current = {
          revision,
          selector,
          value: cached.value,
        };
        return cached.value;
      }

      cacheRef.current = {
        revision,
        selector,
        value: nextValue,
      };
      return nextValue;
    },
    () => selector(model)
  );
}

export function resolveInitialSpecsModel(args: {
  importSpecs: SpecsCatalog | null;
  importSelectedProduct: string;
  importSelectedVersion: string;
  currentProductName: string;
  currentVersionName: string;
}) {
  if (!args.importSpecs) {
    return null;
  }

  const productName = args.importSelectedProduct || args.currentProductName;
  const versionName = args.importSelectedVersion || args.currentVersionName;

  if (!productName || !versionName) {
    return createScratchSpecsModel(versionName || "1.0", args.importSpecs);
  }

  return SpecsModel.fromCatalog(args.importSpecs, productName, versionName);
}
